// Mirrors catalog.categories / catalog.products / product_variant_groups /
// product_variant_options / product_variants / store_inventory /
// inventory_adjustments in CliqSuite_DB_Architecture.dc.html.
import { z } from "zod";

export const categorySchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  sort_order: z.number().int().default(0),
  is_active: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Category = z.infer<typeof categorySchema>;

export const productSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  category_id: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  base_price: z.number().int().default(0),
  cost: z.number().int().default(0),
  // "base" (default) reads cost above for profit calc; "recipe" instead
  // sums this product's own product_ingredients x ingredients.cost. An
  // explicit owner choice, never inferred from whether cost is 0 — see
  // owner-portal's ProductDrawer toggle and routes/index.tsx's
  // effectiveProductCost.
  cost_mode: z.enum(["base", "recipe"]).default("base"),
  emoji: z.string().nullable(),
  image_url: z.string().nullable(),
  is_available: z.boolean().default(true),
  is_featured: z.boolean().default(false),
  is_new: z.boolean().default(false),
  has_variants: z.boolean().default(false),
  is_inventory_item: z.boolean().default(false),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Product = z.infer<typeof productSchema>;

export const productVariantGroupSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  name: z.string(),
  is_required: z.boolean().default(true),
  max_selections: z.number().int().default(1),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProductVariantGroup = z.infer<typeof productVariantGroupSchema>;

export const productVariantOptionSchema = z.object({
  id: z.string().uuid(),
  group_id: z.string().uuid(),
  label: z.string(),
  price_delta: z.number().int().default(0),
  cost_delta: z.number().int().default(0),
  sku_suffix: z.string().nullable(),
  sort_order: z.number().int().default(0),
  is_active: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProductVariantOption = z.infer<typeof productVariantOptionSchema>;

/** Store-owned quick charge available beside a POS cart line. Empty product/category
 * lists mean "all items"; order_types controls where it appears. */
export const addOnSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string().min(1).max(100),
  price: z.number().int().nonnegative(),
  product_ids: z.array(z.string().uuid()).default([]),
  category_ids: z.array(z.string().uuid()).default([]),
  order_types: z.array(z.enum(["counter", "dine_in", "pickup"])).default(["counter", "dine_in", "pickup"]),
  ingredient_id: z.string().uuid().nullable(),
  ingredient_qty: z.number().int().nonnegative().default(0),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AddOn = z.infer<typeof addOnSchema>;

export const productVariantSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  sku: z.string(),
  barcode: z.string().nullable().default(null),
  variant_label: z.string(),
  price_override: z.number().int().nullable(),
  cost_override: z.number().int().nullable(),
  is_active: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProductVariant = z.infer<typeof productVariantSchema>;

export const storeInventorySchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable(),
  quantity: z.number().int().default(0),
  low_stock_threshold: z.number().int().default(0),
  is_out_of_stock: z.boolean().default(false),
  updated_at: z.string(),
});
export type StoreInventory = z.infer<typeof storeInventorySchema>;

export const inventoryAdjustmentSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  product_id: z.string().uuid().nullable(),
  variant_id: z.string().uuid().nullable(),
  adjustment_type: z.enum(["sale", "restock", "manual", "waste", "void"]),
  quantity_delta: z.number().int(),
  quantity_after: z.number().int(),
  reference_order_id: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  performed_by_staff_id: z.string().uuid().nullable(),
  created_at: z.string(),
});
export type InventoryAdjustment = z.infer<typeof inventoryAdjustmentSchema>;

// Raw ingredient-level stock for recipes (restaurant preset) — distinct from
// store_inventory, which tracks sellable products/variants (retail preset).
export const ingredientSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string(),
  storage_location: z.string().default("Dry storage"),
  unit: z.string().default("units"),
  quantity: z.number().int().default(0),
  low_stock_threshold: z.number().int().default(10),
  cost: z.number().int().default(0),
  // Who it is bought from; a supplier's "Supplies" list reads the same column.
  supplier_id: z.string().uuid().nullable().default(null),
  is_active: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Ingredient= z.infer<typeof ingredientSchema>;

// Recipe / bill-of-materials line — how much of one ingredient a menu item
// (product) consumes per unit sold. Mirrors the mockup's `m.recipe` array.
// variant_option_id null = base line (always applies); set = this line only
// applies when that specific variant option is picked, additive on top of
// the base recipe (not a swap/override).
export const productIngredientSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  ingredient_id: z.string().uuid(),
  variant_option_id: z.string().uuid().nullable(),
  qty: z.number().int().default(1),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProductIngredient = z.infer<typeof productIngredientSchema>;

export const supplierSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  name: z.string(),
  contact: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable().default(null),
  payment_terms: z.string().default("Net 30"),
  is_active: z.boolean().default(true),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Supplier = z.infer<typeof supplierSchema>;

export const purchaseOrderSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid(),
  supplier_id: z.string().uuid(),
  ref: z.string(),
  status: z.enum(["draft", "sent", "received", "cancelled"]).default("draft"),
  order_date: z.string(),
  notes: z.string().nullable(),
  expected_date: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

export const purchaseOrderItemSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  purchase_order_id: z.string().uuid(),
  product_id: z.string().uuid().nullable(),
  item_name: z.string(),
  quantity: z.number().int().default(1),
  unit_cost: z.number().int().default(0),
  created_at: z.string(),
});
export type PurchaseOrderItem = z.infer<typeof purchaseOrderItemSchema>;

// Inter-store stock movement — mirrors the mockup's shallow model (a
// transfer is a from/to/item-count record, not itemized line items).
export const stockTransferSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  from_store_id: z.string().uuid(),
  to_store_id: z.string().uuid(),
  ref: z.string(),
  item_count: z.number().int().default(0),
  status: z.enum(["transit", "completed", "cancelled"]).default("transit"),
  created_at: z.string(),
  completed_at: z.string().nullable(),
});
export type StockTransfer = z.infer<typeof stockTransferSchema>;
